import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import React, { createContext, ReactElement, useContext, useState } from 'react';

import { cn } from '@/utils/styles';

import {
  ExpandableTableActionsProps,
  ExpandableTableContextType,
  ExpandableTableHeaderProps,
  ExpandableTableItemProps,
  ExpandableTableProps,
  ExpandableTableTabContentProps,
  ExpandableTableTabsProps,
} from './expandable-table.types';

const ExpandableTableContext = createContext<ExpandableTableContextType>({
  expandedItems: {},
  activeTabsByItem: {},
  toggleItem: () => {},
  setActiveTabForItem: () => {},
});

const ExpandableTable: React.FC<ExpandableTableProps> & {
  Header: React.FC<ExpandableTableHeaderProps>;
  Item: React.FC<ExpandableTableItemProps>;
  Tabs: React.FC<ExpandableTableTabsProps>;
  TabContent: React.FC<ExpandableTableTabContentProps>;
  Actions: React.FC<ExpandableTableActionsProps>;
} = ({ children, className, hideHeader = true, defaultTab = 'Details', ...props }) => {
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [activeTabsByItem, setActiveTabsByItem] = useState<Record<string, string>>({});

  const toggleItem = (id: string) => {
    setExpandedItems((prev) => {
      const willBeExpanded = !prev[id];

      if (willBeExpanded) {
        setActiveTabsByItem((prevTabs) => ({
          ...prevTabs,
          [id]: prevTabs[id] || defaultTab,
        }));
      }

      return {
        ...prev,
        [id]: willBeExpanded,
      };
    });
  };

  const setActiveTabForItem = (itemId: string, tabId: string) => {
    setActiveTabsByItem((prev) => ({
      ...prev,
      [itemId]: tabId,
    }));
  };

  const filteredChildren = hideHeader
    ? React.Children.toArray(children).filter((child) => React.isValidElement(child) && child.type !== Header)
    : children;

  return (
    <ExpandableTableContext.Provider
      value={{
        expandedItems,
        activeTabsByItem,
        toggleItem,
        setActiveTabForItem,
      }}>
      <div
        className={cn(
          'flex flex-col rounded-md border border-NeutralLightDark bg-NeutralLightLight overflow-hidden shadow-sm',
          className,
        )}
        {...props}>
        {filteredChildren}
      </div>
    </ExpandableTableContext.Provider>
  );
};

const Header = ({ children, className, ...props }: ExpandableTableHeaderProps): ReactElement => {
  return (
    <div
      className={cn(
        'bg-NeutralLightMedium px-6 py-4 font-medium border-b border-NeutralLightDark flex items-center text-sm text-NeutralDarkDarkest',
        className,
      )}
      {...props}>
      {children}
    </div>
  );
};

const Item = ({ id, header, children, className, ...props }: ExpandableTableItemProps): ReactElement => {
  const { expandedItems, toggleItem } = useContext(ExpandableTableContext);
  const isExpanded = expandedItems[id] ?? false;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleItem(id);
    }
  };

  return (
    <div className={cn('flex flex-col border-b border-NeutralLightDark last:border-b-0', className)} {...props}>
      <button
        type='button'
        className={cn(
          'flex items-center w-full px-6 py-4 text-left cursor-pointer transition-colors',
          isExpanded ? 'bg-NeutralLightLight' : 'hover:bg-NeutralLightMedium',
        )}
        onClick={() => toggleItem(id)}
        onKeyDown={handleKeyDown}>
        <div className='flex-1 font-medium'>{header}</div>
        <div className={cn('ml-3', isExpanded ? 'text-HighlightDark' : 'text-NeutralDarkLight')}>
          {isExpanded ? (
            <ChevronDownIcon className='w-5 h-5' aria-hidden={true} />
          ) : (
            <ChevronRightIcon className='w-5 h-5' aria-hidden={true} />
          )}
        </div>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0',
        )}>
        {children}
      </div>
    </div>
  );
};

const Tabs = ({
  tabs,
  activeTab,
  onTabChange,
  itemId,
  className,
  actions,
  ...props
}: ExpandableTableTabsProps): ReactElement => {
  const { activeTabsByItem, setActiveTabForItem } = useContext(ExpandableTableContext);

  const getActiveTab = (): string => {
    let activeTab = tabs.length > 0 && tabs[0] ? tabs[0] : '';

    if (itemId) {
      const tabValue = activeTabsByItem[itemId];
      if (typeof tabValue === 'string') {
        activeTab = tabValue;
      }
    }

    return activeTab;
  };

  const currentActiveTab = getActiveTab();

  React.useEffect(() => {
    if (itemId && (!activeTabsByItem[itemId] || activeTabsByItem[itemId] !== currentActiveTab)) {
      setActiveTabForItem(itemId, currentActiveTab);
    }
  }, [itemId, currentActiveTab, activeTabsByItem, setActiveTabForItem]);

  const handleTabChange = (tab: string) => {
    if (itemId) {
      setActiveTabForItem(itemId, tab);
    } else if (onTabChange) {
      onTabChange(tab);
    }
  };

  return (
    <div className={cn('flex flex-row w-full border-b border-NeutralLightDark', className)} {...props} role='tablist'>
      <div className='flex flex-1'>
        {tabs.map((tab) => (
          <button
            key={tab}
            type='button'
            role='tab'
            className={cn(
              'px-6 py-3 text-sm font-medium transition-colors',
              currentActiveTab === tab
                ? 'text-NeutralDarkDarkest border-b-2 border-NeutralDarkDarkest'
                : 'text-NeutralDarkLight hover:text-NeutralDarkMedium hover:bg-NeutralLightMedium',
            )}
            onClick={() => handleTabChange(tab)}>
            {tab}
          </button>
        ))}
      </div>
      {actions && <div className='flex items-center px-4'>{actions}</div>}
    </div>
  );
};

const TabContent = ({
  children,
  tabId,
  activeTabId,
  itemId,
  className,
  ...props
}: ExpandableTableTabContentProps): ReactElement | null => {
  const { activeTabsByItem } = useContext(ExpandableTableContext);

  const currentActiveTabId = itemId ? activeTabsByItem[itemId] : activeTabId;

  if (tabId !== currentActiveTabId) return null;

  return (
    <div className={cn('w-full p-6', className)} role='tabpanel' aria-labelledby={tabId} {...props}>
      {children}
    </div>
  );
};

const Actions = ({ children, className, ...props }: ExpandableTableActionsProps): ReactElement => {
  return (
    <div className={cn('flex flex-shrink-0 items-center justify-end gap-3 ml-auto', className)} {...props}>
      {children}
    </div>
  );
};

ExpandableTable.Header = Header;
ExpandableTable.Item = Item;
ExpandableTable.Tabs = Tabs;
ExpandableTable.TabContent = TabContent;
ExpandableTable.Actions = Actions;

export { ExpandableTable };
